/**
 * @fileoverview Unified inline category editor
 *
 * Single form for creating one or more custom fields in a category and
 * writing their values (plus any sibling values) into a preset — all in
 * one save. Replaces the older "create field → set value" two-step
 * popups in both the letter editor (template-editor) and the PDF field
 * editor (PdfFieldEditor). Every custom field is org-wide and filed
 * under a category — there's no "one-off" scope (those orphan).
 *
 * The component is presentation-only; the actual create/save go through
 * the parent-supplied `onCreateCustomField` / `onSavePreset` handlers.
 * On success it calls `onCreated` with the first new field so the parent
 * can insert it (chip into a letter, or binding for a PDF placement).
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, X } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/utils/misc";
import { Label } from "@/ui/primitives/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import {
  FIELD_CATEGORIES,
  type FieldType,
} from "@cvx/settings/precedents/canonicalFields";

export interface UnifiedCustomFieldDef {
  key: string;
  label: string;
  fieldType: string;
  categoryId: string;
  categoryLabel: string;
}

export interface UnifiedPresetSummary {
  _id: string;
  categoryId: string;
  name: string;
  values: Record<string, string>;
}

export interface UnifiedCreateInput {
  key: string;
  label: string;
  fieldType: FieldType;
  categoryId: string;
  categoryLabel: string;
}

export interface UnifiedSavePresetInput {
  categoryId: string;
  categoryLabel: string;
  values: Record<string, string>;
  presetId?: string;
  newPresetName?: string;
}

interface DraftField {
  tempId: string;
  label: string;
  type: FieldType;
  value: string;
  checkboxMode?: "checked" | "unchecked";
}

function slugifyKey(input: string): string {
  return input
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\s_]/g, "")
    .replace(/\s+/g, "_");
}

function makeTempId(): string {
  return Math.random().toString(36).slice(2, 10);
}

export interface UnifiedFieldCreatorProps {
  /** Org-wide custom fields, used to list siblings + dedupe keys. */
  customFields: UnifiedCustomFieldDef[];
  /** All org presets, filtered internally to the chosen category. */
  orgPresets?: UnifiedPresetSummary[];
  /** Initial field type for the first draft row. */
  initialFieldType?: FieldType;
  /** Wider compact layout (e.g. inside an editor toolbar popover). */
  compact?: boolean;
  /** Persist a new field. */
  onCreateCustomField: (
    input: UnifiedCreateInput,
  ) => Promise<{ key: string; label: string } | null>;
  /** Persist a preset (create or merge-update). Optional — without it,
   *  the form skips the preset/value step and just creates fields. */
  onSavePreset?: (input: UnifiedSavePresetInput) => Promise<void>;
  /** Called with the first new field after save; parent inserts/binds it. */
  onCreated: (field: {
    key: string;
    label: string;
    type: FieldType;
    checkboxMode?: "checked" | "unchecked";
  }) => void;
  onCancel: () => void;
}

export function UnifiedFieldCreator({
  customFields,
  orgPresets,
  initialFieldType = "text",
  compact = false,
  onCreateCustomField,
  onSavePreset,
  onCreated,
  onCancel,
}: UnifiedFieldCreatorProps) {
  const [newCategory, setNewCategory] = useState("");
  const [showNewCategory, setShowNewCategory] = useState(false);
  const [presetChoice, setPresetChoice] = useState<string>("__new__");
  const [newPresetName, setNewPresetName] = useState("Default");
  const [existingFieldValues, setExistingFieldValues] = useState<
    Record<string, string>
  >({});
  const [newFieldDrafts, setNewFieldDrafts] = useState<DraftField[]>([
    { tempId: makeTempId(), label: "", type: initialFieldType, value: "" },
  ]);
  const [submitting, setSubmitting] = useState(false);

  const categoryId = newCategory.trim() ? slugifyKey(newCategory) : "";
  const categoryLabel = newCategory.trim();

  // Unique existing categories from customFields.
  const existingCategories = useMemo(() => {
    const seen = new Map<string, string>();
    for (const cf of customFields) {
      if (!seen.has(cf.categoryId)) seen.set(cf.categoryId, cf.categoryLabel);
    }
    return [...seen.entries()].map(([id, label]) => ({ id, label }));
  }, [customFields]);

  const siblingFields = useMemo(
    () =>
      categoryId
        ? customFields.filter((cf) => cf.categoryId === categoryId)
        : [],
    [customFields, categoryId],
  );

  const presetsInCategory = useMemo(
    () =>
      categoryId
        ? (orgPresets ?? []).filter((p) => p.categoryId === categoryId)
        : [],
    [orgPresets, categoryId],
  );

  // When the chosen preset changes, hydrate sibling-value inputs from
  // its values so siblings the user *doesn't* edit aren't accidentally
  // wiped on save.
  useEffect(() => {
    if (presetChoice === "__new__") {
      setExistingFieldValues({});
      return;
    }
    const preset = presetsInCategory.find((p) => p._id === presetChoice);
    if (!preset) return;
    const next: Record<string, string> = {};
    for (const f of siblingFields) {
      next[f.key] = preset.values[f.key] ?? "";
    }
    setExistingFieldValues(next);
  }, [presetChoice, presetsInCategory, siblingFields]);

  // When category changes, default the preset to the first existing one
  // (so siblings load with values) — or stay on "+ New" for a fresh
  // category with no presets yet.
  useEffect(() => {
    if (!categoryId) return;
    if (presetsInCategory.length > 0) {
      setPresetChoice((cur) =>
        cur === "__new__" || !presetsInCategory.find((p) => p._id === cur)
          ? presetsInCategory[0]._id
          : cur,
      );
    } else {
      setPresetChoice("__new__");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryId]);

  const updateDraft = useCallback(
    (tempId: string, patch: Partial<DraftField>) => {
      setNewFieldDrafts((prev) =>
        prev.map((d) => (d.tempId === tempId ? { ...d, ...patch } : d)),
      );
    },
    [],
  );
  const addDraft = useCallback(() => {
    setNewFieldDrafts((prev) => [
      ...prev,
      { tempId: makeTempId(), label: "", type: "text", value: "" },
    ]);
  }, []);
  const removeDraft = useCallback((tempId: string) => {
    setNewFieldDrafts((prev) => prev.filter((d) => d.tempId !== tempId));
  }, []);

  const handleSave = useCallback(async () => {
    if (submitting) return;
    if (!categoryLabel) {
      toast.error("Pick a category or name a new one.");
      return;
    }
    const validDrafts = newFieldDrafts.filter((d) => d.label.trim() !== "");
    if (validDrafts.length === 0) {
      toast.error("Add at least one field label.");
      return;
    }
    const allExistingKeys = new Set([
      ...customFields.map((c) => c.key),
      ...FIELD_CATEGORIES.flatMap((c) => c.fields.map((f) => f.key)),
    ]);
    const proposed = validDrafts.map((d) => slugifyKey(d.label));
    for (let i = 0; i < proposed.length; i++) {
      if (!proposed[i]) {
        toast.error(`"${validDrafts[i].label}" doesn't make a valid key.`);
        return;
      }
      if (allExistingKeys.has(proposed[i])) {
        toast.error(
          `A field with key "${proposed[i]}" already exists — rename or pick it from the list.`,
        );
        return;
      }
    }
    const isNewPreset = presetChoice === "__new__";
    if (onSavePreset && isNewPreset && !newPresetName.trim()) {
      toast.error("Name the new preset.");
      return;
    }

    setSubmitting(true);
    try {
      const created: Array<{
        key: string;
        label: string;
        draft: DraftField;
      }> = [];
      for (const draft of validDrafts) {
        const result = await onCreateCustomField({
          key: slugifyKey(draft.label),
          label: draft.label.trim(),
          fieldType: draft.type,
          categoryId,
          categoryLabel,
        });
        if (result) created.push({ ...result, draft });
      }
      if (created.length === 0) {
        setSubmitting(false);
        return;
      }
      if (onSavePreset) {
        const values: Record<string, string> = { ...existingFieldValues };
        for (const c of created) values[c.key] = c.draft.value;
        await onSavePreset({
          categoryId,
          categoryLabel,
          values,
          ...(isNewPreset
            ? { newPresetName: newPresetName.trim() }
            : { presetId: presetChoice }),
        });
      }
      const primary = created[0];
      onCreated({
        key: primary.key,
        label: primary.label,
        type: primary.draft.type,
        checkboxMode:
          primary.draft.type === "checkbox"
            ? (primary.draft.checkboxMode ?? "checked")
            : undefined,
      });
    } finally {
      setSubmitting(false);
    }
  }, [
    submitting,
    categoryLabel,
    categoryId,
    customFields,
    newFieldDrafts,
    presetChoice,
    newPresetName,
    existingFieldValues,
    onCreateCustomField,
    onSavePreset,
    onCreated,
  ]);

  const inputTextSize = compact ? "text-[11px]" : "text-xs";

  return (
    <div className="flex max-h-[55vh] flex-col gap-2 overflow-y-auto">
      {/* Category — required so every field is filed somewhere. */}
      <div>
        <Label className="text-[10px]">Category</Label>
        {showNewCategory ? (
          <div className="mt-0.5 flex gap-1">
            <input
              type="text"
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
              placeholder="New category name (e.g. Vehicle Search)"
              className={cn(
                "h-7 flex-1 rounded border border-border bg-background px-2 outline-none focus:border-primary/40 focus:ring-1 focus:ring-primary/20",
                inputTextSize,
              )}
              autoFocus
              onKeyDown={(e) => e.stopPropagation()}
            />
            <button
              type="button"
              className="rounded px-1.5 text-[10px] text-primary/50 hover:bg-secondary"
              onClick={() => {
                setShowNewCategory(false);
                setNewCategory("");
              }}
            >
              Cancel
            </button>
          </div>
        ) : (
          <Select
            value={newCategory === "" ? undefined : newCategory}
            onValueChange={(val) => {
              if (val === "__new__") {
                setShowNewCategory(true);
                setNewCategory("");
              } else {
                setNewCategory(val);
              }
            }}
          >
            <SelectTrigger className={cn("mt-0.5 h-7", inputTextSize)}>
              <SelectValue placeholder="Pick a category…" />
            </SelectTrigger>
            <SelectContent>
              {existingCategories.map((c) => (
                <SelectItem key={c.id} value={c.label}>
                  {c.label}
                </SelectItem>
              ))}
              <SelectItem value="__new__">+ New category…</SelectItem>
            </SelectContent>
          </Select>
        )}
      </div>

      {categoryLabel ? (
        <>
          {/* Preset — fields' values live inside a preset under the category. */}
          {onSavePreset && (
            <div>
              <Label className="text-[10px]">Preset</Label>
              <Select
                value={presetChoice}
                onValueChange={(val) => setPresetChoice(val)}
              >
                <SelectTrigger className={cn("mt-0.5 h-7", inputTextSize)}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {presetsInCategory.map((p) => (
                    <SelectItem key={p._id} value={p._id}>
                      {p.name}
                    </SelectItem>
                  ))}
                  <SelectItem value="__new__">+ New preset…</SelectItem>
                </SelectContent>
              </Select>
              {presetChoice === "__new__" && (
                <input
                  type="text"
                  value={newPresetName}
                  onChange={(e) => setNewPresetName(e.target.value)}
                  placeholder='Preset name (e.g. "Default")'
                  className={cn(
                    "mt-1 h-7 w-full rounded border border-border bg-background px-2 outline-none focus:border-primary/40 focus:ring-1 focus:ring-primary/20",
                    inputTextSize,
                  )}
                  onKeyDown={(e) => e.stopPropagation()}
                />
              )}
            </div>
          )}

          {/* Existing siblings — pre-filled from the chosen preset so the
              user sees what's already in the category. */}
          {onSavePreset && siblingFields.length > 0 && (
            <div>
              <Label className="text-[10px]">
                Existing fields in {categoryLabel}
              </Label>
              <div className="mt-1 space-y-1">
                {siblingFields.map((f) => (
                  <div
                    key={f.key}
                    className="grid grid-cols-[1fr_1.4fr] items-center gap-2"
                  >
                    <span
                      className="truncate text-[11px] text-primary/70"
                      title={f.key}
                    >
                      {f.label}
                    </span>
                    {f.fieldType === "checkbox" ? (
                      <label className="flex h-7 cursor-pointer items-center gap-2 rounded border border-border px-2 text-[11px]">
                        <input
                          type="checkbox"
                          checked={
                            existingFieldValues[f.key] === "true" ||
                            existingFieldValues[f.key] === "yes" ||
                            existingFieldValues[f.key] === "1"
                          }
                          onChange={(e) =>
                            setExistingFieldValues((prev) => ({
                              ...prev,
                              [f.key]: e.target.checked ? "true" : "false",
                            }))
                          }
                          className="size-3.5 rounded border-primary/20 text-primary accent-primary"
                        />
                        <span className="text-primary/60">
                          {existingFieldValues[f.key] === "true"
                            ? "Checked"
                            : "Unchecked"}
                        </span>
                      </label>
                    ) : (
                      <input
                        type={f.fieldType === "date" ? "date" : "text"}
                        inputMode={
                          f.fieldType === "number" ? "decimal" : undefined
                        }
                        value={existingFieldValues[f.key] ?? ""}
                        onChange={(e) =>
                          setExistingFieldValues((prev) => ({
                            ...prev,
                            [f.key]: e.target.value,
                          }))
                        }
                        placeholder="(no value)"
                        className="h-7 rounded border border-border bg-background px-2 text-[11px] outline-none focus:border-primary/40 focus:ring-1 focus:ring-primary/20"
                        onKeyDown={(e) => e.stopPropagation()}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* New fields being added in this batch. */}
          <div>
            <Label className="text-[10px]">New fields you&apos;re adding</Label>
            <div className="mt-1 space-y-2">
              {newFieldDrafts.map((draft) => (
                <div
                  key={draft.tempId}
                  className="rounded border border-dashed border-border/60 bg-background/60 p-2"
                >
                  <div className="flex items-center gap-1">
                    <input
                      type="text"
                      value={draft.label}
                      onChange={(e) =>
                        updateDraft(draft.tempId, { label: e.target.value })
                      }
                      placeholder="Field label"
                      className="h-7 flex-1 rounded border border-border bg-background px-2 text-[11px] outline-none focus:border-primary/40 focus:ring-1 focus:ring-primary/20"
                      onKeyDown={(e) => e.stopPropagation()}
                    />
                    {newFieldDrafts.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeDraft(draft.tempId)}
                        className="rounded p-1 text-[10px] text-primary/40 hover:bg-secondary hover:text-destructive"
                        aria-label="Remove field"
                      >
                        <X className="size-3" />
                      </button>
                    )}
                  </div>
                  <div className="mt-1 flex gap-1">
                    {(["text", "checkbox", "date", "number"] as const).map(
                      (ft) => (
                        <button
                          key={ft}
                          type="button"
                          onClick={() =>
                            updateDraft(draft.tempId, {
                              type: ft as FieldType,
                            })
                          }
                          className={cn(
                            "flex-1 rounded border px-2 py-1 text-[10px] font-medium transition",
                            draft.type === ft
                              ? "border-primary bg-primary/10 text-primary"
                              : "border-border text-muted-foreground hover:border-primary/30",
                          )}
                        >
                          {ft.charAt(0).toUpperCase() + ft.slice(1)}
                        </button>
                      ),
                    )}
                  </div>
                  {onSavePreset && (
                    <div className="mt-1">
                      {draft.type === "checkbox" ? (
                        <label className="flex h-7 cursor-pointer items-center gap-2 rounded border border-border px-2 text-[11px]">
                          <input
                            type="checkbox"
                            checked={
                              draft.value === "true" ||
                              draft.value === "yes" ||
                              draft.value === "1"
                            }
                            onChange={(e) =>
                              updateDraft(draft.tempId, {
                                value: e.target.checked ? "true" : "false",
                              })
                            }
                            className="size-3.5 rounded border-primary/20 text-primary accent-primary"
                          />
                          <span className="text-primary/60">
                            {draft.value === "true" ? "Checked" : "Unchecked"}
                          </span>
                        </label>
                      ) : (
                        <input
                          type={draft.type === "date" ? "date" : "text"}
                          inputMode={
                            draft.type === "number" ? "decimal" : undefined
                          }
                          value={draft.value}
                          onChange={(e) =>
                            updateDraft(draft.tempId, {
                              value: e.target.value,
                            })
                          }
                          placeholder="Value for this preset"
                          className="h-7 w-full rounded border border-border bg-background px-2 text-[11px] outline-none focus:border-primary/40 focus:ring-1 focus:ring-primary/20"
                          onKeyDown={(e) => e.stopPropagation()}
                        />
                      )}
                    </div>
                  )}
                  {draft.label.trim() && (
                    <span className="mt-1 block font-mono text-[9px] text-muted-foreground">
                      key: {slugifyKey(draft.label)}
                    </span>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={addDraft}
                className="flex w-full items-center justify-center gap-1.5 rounded border border-dashed border-border/60 px-2 py-1.5 text-[11px] text-primary/60 transition hover:border-primary/40 hover:bg-secondary hover:text-primary"
              >
                <Plus className="size-3" />
                Add another field
              </button>
            </div>
          </div>
        </>
      ) : (
        <p className="text-[10px] text-primary/40">
          Pick or name a category to start adding fields and values.
        </p>
      )}

      <div className="sticky bottom-0 flex items-center justify-end gap-1.5 border-t border-border bg-secondary/30 px-2 py-1.5">
        <button
          type="button"
          onClick={onCancel}
          disabled={submitting}
          className="rounded px-2 py-1 text-[11px] text-primary/60 hover:bg-secondary disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => void handleSave()}
          disabled={
            submitting ||
            !categoryLabel ||
            newFieldDrafts.every((d) => !d.label.trim()) ||
            (onSavePreset !== undefined &&
              presetChoice === "__new__" &&
              !newPresetName.trim())
          }
          className="rounded bg-primary px-2 py-1 text-[11px] font-medium text-primary-foreground transition hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {submitting ? "Saving…" : "Save & insert"}
        </button>
      </div>
    </div>
  );
}
